import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  CContainer,
  CRow,
  CCol,
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
  CSpinner,
  CFormSelect,
  CFormInput,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CFormCheck,
  CBadge,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
  cilArrowLeft,
  cilSave,
  cilTrash,
  cilInfo,
  cilList,
  cibLinuxFoundation,
} from "@coreui/icons";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import CustomVideoPlayer from "../../components/CustomVideoPlayer/CustomVideoPlayer";
import ThinkingOrb from "../../components/ThinkingOrb/ThinkingOrb";
import "./MarkupPage.css";

const getDeviceOffsetMin = (offsetMsk) =>
  (3 + (parseInt(offsetMsk, 10) || 0)) * 60;
const VIDEO_FPS = 24.0;
const isSuperuser = localStorage.getItem("is_superuser") === "true";
const canDoMarkup = localStorage.getItem("can_do_markup") === "true";

// Точный перевод секунд плеера в метку времени БД (миллисекунды) с плавной интерполяцией
const videoSecToDbMs = (sec, mapping, fallbackStartMs) => {
  if (!mapping || mapping.length === 0) return fallbackStartMs + sec * 1000;
  const targetF = Math.round(sec * VIDEO_FPS);

  let l = 0,
    r = mapping.length - 1;
  while (l <= r) {
    const m = Math.floor((l + r) / 2);
    if (mapping[m].f === targetF) return new Date(mapping[m].t).getTime();
    if (mapping[m].f < targetF) l = m + 1;
    else r = m - 1;
  }
  if (l >= mapping.length)
    return new Date(mapping[mapping.length - 1].t).getTime();
  if (r < 0) return new Date(mapping[0].t).getTime();

  const p1 = mapping[Math.max(0, l - 1)];
  const p2 = mapping[Math.min(mapping.length - 1, l)];
  if (p2.f === p1.f) return new Date(p1.t).getTime();

  const factor = (targetF - p1.f) / (p2.f - p1.f);
  const t1 = new Date(p1.t).getTime();
  const t2 = new Date(p2.t).getTime();
  return t1 + (t2 - t1) * factor;
};

// Точный перевод времени с таймлайна (мс) в секунды видеоплеера
const dbMsToVideoSec = (ms, mapping, fallbackStartMs) => {
  if (!mapping || mapping.length === 0)
    return Math.max(0, (ms - fallbackStartMs) / 1000);

  let l = 0,
    r = mapping.length - 1;
  while (l <= r) {
    const m = Math.floor((l + r) / 2);
    const mTime = new Date(mapping[m].t).getTime();
    if (mTime === ms) return mapping[m].f / VIDEO_FPS;
    if (mTime < ms) l = m + 1;
    else r = m - 1;
  }
  if (l >= mapping.length) return mapping[mapping.length - 1].f / VIDEO_FPS;
  if (r < 0) return mapping[0].f / VIDEO_FPS;

  const p1 = mapping[Math.max(0, l - 1)];
  const p2 = mapping[Math.min(mapping.length - 1, l)];
  const t1 = new Date(p1.t).getTime();
  const t2 = new Date(p2.t).getTime();
  if (t2 === t1) return p1.f / VIDEO_FPS;

  const factor = (ms - t1) / (t2 - t1);
  return (p1.f + (p2.f - p1.f) * factor) / VIDEO_FPS;
};

const SyncEditableTimeline = ({
  markups,
  setMarkups,
  aiSessions,
  blePoints,
  initialStart,
  initialEnd,
  videoTimeMs,
  activeStartMs,
  targetHourStart,
  targetHourEnd,
  timezoneOffset,
  onSeekDoubleClick,
}) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [viewStart, setViewStart] = useState(initialStart);
  const [viewEnd, setViewEnd] = useState(initialEnd);
  const [containerWidth, setContainerWidth] = useState(1000);

  const [isPanning, setIsPanning] = useState(false);
  const [lastMouseX, setLastMouseX] = useState(0);
  const [dragState, setDragState] = useState(null);

  const [hoveredBle, setHoveredBle] = useState(null);

  const viewDuration = viewEnd - viewStart;

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      if (entries[0]) setContainerWidth(entries[0].contentRect.width);
    });
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (
      initialStart &&
      initialEnd &&
      (initialStart !== viewStart || initialEnd !== viewEnd)
    ) {
      setViewStart(initialStart);
      setViewEnd(initialEnd);
    }
  }, [initialStart, initialEnd]);

  const decimatedBlePoints = useMemo(() => {
    if (
      !blePoints ||
      blePoints.length === 0 ||
      !containerWidth ||
      viewDuration <= 0
    )
      return [];

    let l = 0,
      r = blePoints.length - 1;
    let startIdx = 0;
    while (l <= r) {
      let m = Math.floor((l + r) / 2);
      if (blePoints[m].tsMs < viewStart) {
        l = m + 1;
      } else {
        startIdx = m;
        r = m - 1;
      }
    }

    const msPerPixel = viewDuration / containerWidth;
    const bucketSizeMs = msPerPixel * 2.5;
    const buckets = new Map();

    for (let i = startIdx; i < blePoints.length; i++) {
      const pt = blePoints[i];
      if (pt.tsMs > viewEnd) break;

      const bucketIdx = Math.floor(pt.tsMs / bucketSizeMs);
      const key = `${bucketIdx}_${pt.l}`;

      const existing = buckets.get(key);
      if (!existing || pt.r > existing.r) {
        buckets.set(key, pt);
      }
    }

    return Array.from(buckets.values());
  }, [blePoints, viewStart, viewEnd, containerWidth, viewDuration]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = containerWidth;
    canvas.height = 120;

    const ctx = canvas.getContext("2d", { alpha: false });
    ctx.fillStyle = "#1e1e2f";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const MIN_RSSI = -110;
    const MAX_RSSI = -40;

    ctx.strokeStyle = "rgba(255, 255, 255, 0.15)";
    ctx.fillStyle = "rgba(255, 255, 255, 0.6)";
    ctx.font = "10px Arial";
    ctx.textAlign = "left";

    const levels = [-40, -60, -80, -100];
    ctx.beginPath();
    levels.forEach((level) => {
      let normalized = (level - MIN_RSSI) / (MAX_RSSI - MIN_RSSI);
      let y = Math.floor(canvas.height - normalized * canvas.height) + 0.5;
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.fillText(`${level} dBm`, 5, y > 10 ? y - 3 : y + 12);
    });
    ctx.stroke();

    if (!decimatedBlePoints.length) return;

    decimatedBlePoints.forEach((pt) => {
      const x = ((pt.tsMs - viewStart) / viewDuration) * canvas.width;
      let normalizedRssi = (pt.r - MIN_RSSI) / (MAX_RSSI - MIN_RSSI);
      normalizedRssi = Math.max(0, Math.min(1, normalizedRssi));
      const y = canvas.height - normalizedRssi * canvas.height;

      const isExactHovered =
        hoveredBle &&
        hoveredBle.data &&
        hoveredBle.data.tsMs === pt.tsMs &&
        hoveredBle.data.r === pt.r &&
        hoveredBle.data.l === pt.l;

      const isSameLabel =
        hoveredBle && hoveredBle.data && hoveredBle.data.l === pt.l;

      ctx.save();
      if (hoveredBle) {
        ctx.globalAlpha = isSameLabel ? 1.0 : 0.05;
      } else {
        ctx.globalAlpha = 1.0;
      }

      ctx.fillStyle = pt.c;
      ctx.beginPath();
      const radius = isExactHovered ? 6 : 3;
      ctx.arc(x, y, radius, 0, 2 * Math.PI);
      ctx.fill();

      ctx.strokeStyle = isExactHovered
        ? "rgba(255, 255, 255, 0.9)"
        : "rgba(0, 0, 0, 0.5)";
      ctx.lineWidth = isExactHovered ? 1.5 : 1;
      ctx.stroke();
      ctx.restore();
    });
  }, [decimatedBlePoints, viewStart, viewEnd, containerWidth, viewDuration, hoveredBle]);

  const timeToPercent = (time) => ((time - viewStart) / viewDuration) * 100;
  const xToTime = (x) => viewStart + (x / containerWidth) * viewDuration;

  const handleBleMouseMove = useCallback(
    (e) => {
      if (!canvasRef.current || decimatedBlePoints.length === 0) return;

      const rect = canvasRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const MIN_RSSI = -110;
      const MAX_RSSI = -40;
      const canvasHeight = 120;

      let closest = null;
      let minDistance = 12;

      decimatedBlePoints.forEach((pt) => {
        const ptX = ((pt.tsMs - viewStart) / viewDuration) * containerWidth;
        let normalizedRssi = (pt.r - MIN_RSSI) / (MAX_RSSI - MIN_RSSI);
        normalizedRssi = Math.max(0, Math.min(1, normalizedRssi));
        const ptY = canvasHeight - normalizedRssi * canvasHeight;

        if (Math.abs(mouseX - ptX) > minDistance) return;

        const dist = Math.sqrt(
          Math.pow(mouseX - ptX, 2) + Math.pow(mouseY - ptY, 2),
        );

        if (dist < minDistance) {
          minDistance = dist;
          closest = pt;
        }
      });

      if (closest) {
        setHoveredBle({ x: e.clientX, y: e.clientY, data: closest });
      } else {
        setHoveredBle(null);
      }
    },
    [decimatedBlePoints, viewStart, viewDuration, containerWidth],
  );

  const performZoom = useCallback(
    (zoomFactor, anchorXPercent = 0.5) => {
      const minZoom = 15000;
      const maxZoom = 31536000000;
      let newDuration = Math.max(
        minZoom,
        Math.min(maxZoom, viewDuration * zoomFactor),
      );
      const anchorTime = viewStart + viewDuration * anchorXPercent;
      const newStart = anchorTime - newDuration * anchorXPercent;
      setViewStart(newStart);
      setViewEnd(newStart + newDuration);
    },
    [viewDuration, viewStart],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleWheel = (e) => {
      e.preventDefault();
      const rect = container.getBoundingClientRect();
      performZoom(
        e.deltaY > 0 ? 1.3 : 0.77,
        (e.clientX - rect.left) / rect.width,
      );
    };
    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => container.removeEventListener("wheel", handleWheel);
  }, [performZoom]);

  const handleDoubleClickBg = (e) => {
    if (dragState || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const exactTimeClicked = xToTime(e.clientX - rect.left);
    onSeekDoubleClick(exactTimeClicked);
  };

  const handlePointerDownBg = (e) => {
    if (dragState) return;
    setIsPanning(true);
    setLastMouseX(e.clientX);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMoveBg = (e) => {
    if (isPanning) {
      const timeDelta =
        ((e.clientX - lastMouseX) / containerWidth) * viewDuration;
      setLastMouseX(e.clientX);
      setViewStart((prev) => prev - timeDelta);
      setViewEnd((prev) => prev - timeDelta);
    }
  };

  const handlePointerUpBg = (e) => {
    setIsPanning(false);
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  const handlePointerDownMarkup = (e, type, id) => {
    e.stopPropagation();
    const rect = containerRef.current.getBoundingClientRect();
    const timeClicked = xToTime(e.clientX - rect.left);
    const item = markups.find((m) => m.id === id);

    setDragState({
      type,
      id,
      startOffsetMs: timeClicked - item.start,
      originalDuration: item.end - item.start,
    });

    window.addEventListener("pointermove", handleWindowPointerMove);
    window.addEventListener("pointerup", handleWindowPointerUp);
  };

  const handleWindowPointerMove = (e) => {
    setDragState((prevDrag) => {
      if (!prevDrag || !containerRef.current) return prevDrag;
      const rect = containerRef.current.getBoundingClientRect();
      let currentTime = xToTime(e.clientX - rect.left);

      setMarkups((prevMarkups) =>
        prevMarkups.map((m) => {
          if (m.id !== prevDrag.id) return m;
          let newStart = m.start;
          let newEnd = m.end;

          if (prevDrag.type === "move") {
            newStart = currentTime - prevDrag.startOffsetMs;
            newEnd = newStart + prevDrag.originalDuration;
          } else if (prevDrag.type === "resizeLeft") {
            newStart = Math.min(currentTime, m.end - 1000);
          } else if (prevDrag.type === "resizeRight") {
            newEnd = Math.max(currentTime, m.start + 1000);
          }
          return { ...m, start: newStart, end: newEnd };
        }),
      );
      return prevDrag;
    });
  };

  const handleWindowPointerUp = () => {
    setDragState(null);
    window.removeEventListener("pointermove", handleWindowPointerMove);
    window.removeEventListener("pointerup", handleWindowPointerUp);
  };

  const handleRightClickMarkup = (e, id) => {
    e.preventDefault();
    setMarkups((prev) => prev.filter((m) => m.id !== id));
  };

  // Вычисление рисок с точным приведением к часовому поясу устройства
  const tzOffsetMin = getDeviceOffsetMin(timezoneOffset);
  const ticks = [];
  let step = 60000;
  if (viewDuration > 7200000) step = 900000;
  if (viewDuration > 21600000) step = 3600000;

  let currentTick = Math.floor(viewStart / step) * step;
  while (currentTick <= viewEnd) {
    if (currentTick >= viewStart) {
      const tickDate = dayjs(currentTick).utcOffset(tzOffsetMin);
      ticks.push({
        time: currentTick,
        label: tickDate.format(viewDuration < 600000 ? "HH:mm:ss" : "HH:mm"),
      });
    }
    currentTick += step;
  }

  return (
    <div
      className="sync-timeline-container"
      ref={containerRef}
      onDoubleClick={handleDoubleClickBg}
      onPointerDown={handlePointerDownBg}
      onPointerMove={handlePointerMoveBg}
      onPointerUp={handlePointerUpBg}
      onPointerCancel={handlePointerUpBg}
      onMouseLeave={() => setHoveredBle(null)}
    >
      {hoveredBle && (
        <div
          style={{
            position: "fixed",
            top: hoveredBle.y - 50,
            left: hoveredBle.x + 15,
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            color: "white",
            padding: "8px 12px",
            borderRadius: "6px",
            fontSize: "13px",
            pointerEvents: "none",
            zIndex: 9999,
            boxShadow: "0 4px 10px rgba(0,0,0,0.5)",
            whiteSpace: "nowrap",
          }}
        >
          <div className="d-flex align-items-center mb-1">
            <div
              style={{
                width: "12px",
                height: "12px",
                backgroundColor: hoveredBle.data.c,
                borderRadius: "50%",
                marginRight: "8px",
                border: "1px solid white",
              }}
            ></div>
            <strong>{hoveredBle.data.l}</strong>
          </div>
          <div className="text-info fw-bold">
            Сигнал: {hoveredBle.data.r} dBm
          </div>
        </div>
      )}

      {targetHourStart && targetHourEnd && (
        <div
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${Math.max(0, timeToPercent(targetHourStart))}%`,
            width: `${Math.max(0, Math.min(100, timeToPercent(targetHourEnd)) - Math.max(0, timeToPercent(targetHourStart)))}%`,
            backgroundColor: "rgba(40, 167, 69, 0.15)",
            borderLeft: "2px dashed #28a745",
            borderRight: "2px dashed #28a745",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
      )}

      {(() => {
        const scrubberLeft = timeToPercent(videoTimeMs);
        if (scrubberLeft >= -5 && scrubberLeft <= 105) {
          return (
            <div
              className="sync-scrubber"
              style={{ left: `${scrubberLeft}%` }}
            />
          );
        }
        return null;
      })()}

      <div className="sync-timeline-axis">
        {ticks.map((tick) => (
          <div
            key={tick.time}
            className="sync-timeline-tick"
            style={{ left: `${timeToPercent(tick.time)}%` }}
          >
            {tick.label}
          </div>
        ))}
      </div>

      <div className="sync-timeline-track">
        <div className="sync-track-label">Ручная разметка (Вы)</div>
        {markups.map((m) => {
          const l = Math.max(0, timeToPercent(m.start));
          const r = Math.min(100, timeToPercent(m.end));
          if (r < 0 || l > 100) return null;
          return (
            <div
              key={m.id}
              className="sync-manual-item"
              style={{ left: `${l}%`, width: `${r - l}%` }}
              onPointerDown={(e) => handlePointerDownMarkup(e, "move", m.id)}
              onContextMenu={(e) => handleRightClickMarkup(e, m.id)}
            >
              <div
                className="sync-resize-handle left"
                onPointerDown={(e) =>
                  handlePointerDownMarkup(e, "resizeLeft", m.id)
                }
              />
              <div
                className="sync-resize-handle right"
                onPointerDown={(e) =>
                  handlePointerDownMarkup(e, "resizeRight", m.id)
                }
              />
            </div>
          );
        })}
        {activeStartMs !== null && (
          <div
            style={{
              position: "absolute",
              top: "10px",
              height: "25px",
              backgroundColor: "#f9b115",
              left: `${timeToPercent(activeStartMs)}%`,
              width: `${Math.max(1, timeToPercent(videoTimeMs) - timeToPercent(activeStartMs))}%`,
              zIndex: 10,
              pointerEvents: "none",
              borderRadius: "4px",
            }}
          />
        )}
      </div>

      <div className="sync-timeline-ble-track" onMouseMove={handleBleMouseMove}>
        <div className="sync-track-label light">BLE Сигналы машин (RSSI)</div>
        <canvas ref={canvasRef} className="sync-ble-canvas"></canvas>
      </div>

      <div className="sync-timeline-track" style={{ height: "35px" }}>
        <div className="sync-track-label">Нейросеть (БД)</div>
        {aiSessions.map((ai) => {
          const l = Math.max(0, timeToPercent(ai.start));
          const r = Math.min(100, timeToPercent(ai.end));
          if (r < 0 || l > 100) return null;
          return (
            <div
              key={ai.id}
              className="sync-ai-item"
              style={{ left: `${l}%`, width: `${r - l}%` }}
              title={`ИИ: ${ai.cycles} ковш.`}
            >
              {r - l > 3 ? `${ai.cycles}к.` : ""}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MarkupPage = () => {
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [folders, setFolders] = useState([]);
  const [orgFilter, setOrgFilter] = useState("");
  const [deviceSearchQuery, setDeviceSearchQuery] = useState("");
  const [syncingOmni, setSyncingOmni] = useState(false);
  const [omniCooldown, setOmniCooldown] = useState(0);

  const currentDevice = useMemo(() => {
    return devices.find((d) => d.id.toString() === selectedDeviceId);
  }, [devices, selectedDeviceId]);

  useEffect(() => {
    if (omniCooldown > 0) {
      const timer = setTimeout(() => setOmniCooldown((prev) => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [omniCooldown]);

  const uniqueOrganizations = useMemo(() => {
    if (!isSuperuser) return [];
    const orgs = devices.map((d) => d.organization_name).filter(Boolean);
    return [...new Set(orgs)].sort();
  }, [devices, isSuperuser]);

  const filteredDevices = useMemo(() => {
    return devices.filter((dev) => {
      const matchesSearch = dev.name
        .toLowerCase()
        .includes(deviceSearchQuery.toLowerCase());
      const matchesOrg =
        isSuperuser && orgFilter ? dev.organization_name === orgFilter : true;
      return matchesSearch && matchesOrg;
    });
  }, [devices, deviceSearchQuery, orgFilter, isSuperuser]);

  const [selectedFolderId, setSelectedFolderId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [predicting, setPredicting] = useState(false);
  const [predictingBle, setPredictingBle] = useState(false);
  const [isTimelineLoading, setIsTimelineLoading] = useState(false);

  const [labeledSummary, setLabeledSummary] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const [videoUrl, setVideoUrl] = useState(null);
  const [framesMapping, setFramesMapping] = useState([]);
  const [currentVideoDbTimeMs, setCurrentVideoDbTimeMs] = useState(0);
  const [folderStartTime, setFolderStartTime] = useState(null);
  const playerRef = useRef(null);

  const [seekToTime, setSeekToTime] = useState(null);
  const [seekTriggerId, setSeekTriggerId] = useState(0);

  const [visibleTimeStart, setVisibleTimeStart] = useState(Date.now());
  const [visibleTimeEnd, setVisibleTimeEnd] = useState(Date.now() + 3600000);

  const [markups, setMarkups] = useState([]);
  const [aiSessions, setAiSessions] = useState([]);
  const [activeStartMs, setActiveStartMs] = useState(null);
  const [datasetSplit, setDatasetSplit] = useState("train");

  const [blePoints, setBlePoints] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);

  const IDLE_TIMEOUT_MS = 120000;
  const [activeTimeSec, setActiveTimeSec] = useState(0);
  const [isUserActive, setIsUserActive] = useState(false);
  const lastActivityRef = useRef(Date.now());

  const getTagColor = (tag) => {
    if (!blePoints || blePoints.length === 0) return "#888888";
    let pt = blePoints.find((p) => p.l === tag.truck);
    if (!pt) pt = blePoints.find((p) => p.l === tag.mac);
    return pt ? pt.c : "#888888";
  };

  useEffect(() => {
    const handleActivity = () => {
      lastActivityRef.current = Date.now();
      if (!isUserActive) setIsUserActive(true);
    };

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("click", handleActivity);

    const timer = setInterval(() => {
      if (selectedFolderId && videoUrl) {
        const now = Date.now();
        if (now - lastActivityRef.current < IDLE_TIMEOUT_MS) {
          setActiveTimeSec((prev) => prev + 1);
          setIsUserActive(true);
        } else {
          setIsUserActive(false);
        }
      } else {
        setIsUserActive(false);
      }
    }, 1000);

    return () => {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("click", handleActivity);
      clearInterval(timer);
    };
  }, [selectedFolderId, videoUrl, isUserActive]);

  const formatActiveTime = (totalSeconds) => {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}м ${s}с`;
  };

  useEffect(() => {
    if (!isSuperuser && !canDoMarkup) {
      window.location.href = "/dashboard";
      return;
    }
    fetchDevices();
  }, [isSuperuser, canDoMarkup]);

  const fetchDevices = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get("/api/devices/?for_markup=true", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDevices(res.data.results || res.data);
      fetchLabeledSummary();
    } catch (err) {
      toast.error("Ошибка загрузки устройств");
    }
  };

  const fetchLabeledSummary = async () => {
    setLoadingSummary(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get("/api/markup/summary/", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLabeledSummary(res.data);
    } catch (err) {
      toast.error("Ошибка при загрузке бокового меню");
    } finally {
      setLoadingSummary(false);
    }
  };

  const simulateFolderChange = (
    folderIdStr,
    currentFolders,
    currentDeviceId,
  ) => {
    setSelectedFolderId(folderIdStr);
    setActiveTimeSec(0);

    setMarkups([]);
    setAiSessions([]);
    setBlePoints([]);
    setFramesMapping([]);

    if (!folderIdStr) {
      setVideoUrl(null);
      return;
    }

    const folder = currentFolders.find((f) => f.id === parseInt(folderIdStr));
    if (folder && folder.video_file) {
      setVideoUrl(folder.video_file);
      const tzOffset =
        devices.find((d) => d.id === parseInt(currentDeviceId))
          ?.timezone_offset || 0;
      const end = dayjs(folder.process_after)
        .utcOffset(getDeviceOffsetMin(tzOffset))
        .startOf("hour");
      const start = end.subtract(1, "hour");
      setFolderStartTime(start);
      fetchMarkupsAndMapping(folderIdStr, start);
    } else {
      setVideoUrl(null);
      toast.warn("У этой папки еще нет видеофайла");
    }
  };

  const fetchFolders = async (deviceId, autoSelectFolderId = null) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`/api/devices/${deviceId}/folders/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFolders(res.data);

      if (autoSelectFolderId) {
        simulateFolderChange(autoSelectFolderId.toString(), res.data, deviceId);
      } else {
        setSelectedFolderId("");
        setVideoUrl(null);
        setMarkups([]);
        setAiSessions([]);
        setFramesMapping([]);
        setDatasetSplit("train");
        setBlePoints([]);
        setAvailableTags([]);
      }
    } catch (err) {
      toast.error("Ошибка загрузки папок");
    } finally {
      setLoading(false);
    }
  };

  const handleDeviceChange = (e) => {
    const id = e.target.value;
    setSelectedDeviceId(id);
    if (id) fetchFolders(id);
  };

  const handleFolderChange = (e) => {
    simulateFolderChange(e.target.value, folders, selectedDeviceId);
  };

  const handleSelectFromSummary = (deviceId, folderId) => {
    setSelectedDeviceId(deviceId.toString());
    fetchFolders(deviceId, folderId);
  };

  const fetchMarkupsAndMapping = async (folderId, startDt) => {
    setIsTimelineLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`/api/folders/${folderId}/markup/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const mapping = res.data.frames_mapping || [];
      setFramesMapping(mapping);

      const sMs = startDt.valueOf();
      setVisibleTimeStart(sMs - 600000);
      setVisibleTimeEnd(sMs + 3600000 + 600000);

      const loadedMarkups = (res.data.markups || []).map((seg, idx) => ({
        id: `m_${idx}_${Date.now()}`,
        start: new Date(seg.start_time).getTime(),
        end: new Date(seg.end_time).getTime(),
        ble_device_id: seg.ble_device_id || "",
      }));
      setMarkups(loadedMarkups);

      if (res.data.markups && res.data.markups.length > 0) {
        setDatasetSplit(res.data.markups[0].dataset_split || "train");
      } else {
        setDatasetSplit("train");
      }

      const loadedAi = (res.data.ai_sessions || []).map((s, idx) => ({
        id: `ai_${idx}`,
        start: new Date(s.start_time).getTime(),
        end: new Date(s.end_time).getTime(),
        cycles: s.cycles,
      }));
      setAiSessions(loadedAi);

      if (res.data.ble_points) {
        const parsedBle = res.data.ble_points
          .map((pt) => ({
            ...pt,
            tsMs: new Date(pt.t).getTime(),
          }))
          .sort((a, b) => a.tsMs - b.tsMs);
        setBlePoints(parsedBle);
      } else {
        setBlePoints([]);
      }

      if (res.data.available_tags) {
        setAvailableTags(res.data.available_tags);
      }
    } catch (err) {
      console.error("Ошибка загрузки данных", err);
    } finally {
      setIsTimelineLoading(false);
    }
  };

  const saveMarkups = async () => {
    if (!selectedFolderId) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const payload = markups.map((seg) => ({
        start_time: new Date(seg.start).toISOString(),
        end_time: new Date(seg.end).toISOString(),
        ble_device_id: seg.ble_device_id || null,
      }));

      await axios.post(
        `/api/folders/${selectedFolderId}/markup/`,
        {
          segments: payload,
          dataset_split: datasetSplit,
          active_time_seconds: activeTimeSec,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      toast.success("Разметка успешно сохранена!");
      setActiveTimeSec(0);

      fetchFolders(selectedDeviceId, selectedFolderId);
      fetchLabeledSummary();
    } catch (err) {
      toast.error("Ошибка сохранения разметки");
    } finally {
      setSaving(false);
    }
  };

  const handleAIPredict = async () => {
    if (!selectedFolderId) return;
    setPredicting(true);
    try {
      const token = localStorage.getItem("token");

      const res = await axios.get(
        `/api/folders/${selectedFolderId}/predict-ai/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const rawSegments = res.data.segments;
      if (!rawSegments || rawSegments.length === 0) {
        setMarkups([]);
        toast.info("ИИ не нашел заездов в выбранном часе.");
        setPredicting(false);
        return;
      }

      const tempSegments = rawSegments.map((seg, idx) => ({
        id: `ai_pred_${idx}_${Date.now()}`,
        start: new Date(seg.start_time).getTime(),
        end: new Date(seg.end_time).getTime(),
        ble_device_id: "",
      }));

      const payload = tempSegments.map((m) => ({
        id: m.id,
        start_time: new Date(m.start).toISOString(),
        end_time: new Date(m.end).toISOString(),
      }));

      const bleRes = await axios.post(
        `/api/folders/${selectedFolderId}/predict-ble/`,
        {
          segments: payload,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const finalMarkups = tempSegments.map((m) => {
        const matched = bleRes.data.segments.find(
          (updatedSeg) => updatedSeg.id === m.id,
        );
        return {
          ...m,
          ble_device_id: matched ? matched.ble_device_id || "" : "",
        };
      });

      setMarkups(finalMarkups);
      toast.success(
        `ИИ предсказал ${finalMarkups.length} заездов и успешно привязал к ним BLE метки!`,
      );
    } catch (err) {
      toast.error(
        "Ошибка авторазметки ИИ: " + (err.response?.data?.error || err.message),
      );
    } finally {
      setPredicting(false);
    }
  };

  const handleBLEPredict = async () => {
    if (!selectedFolderId || markups.length === 0) return;
    setPredictingBle(true);
    try {
      const token = localStorage.getItem("token");
      const payload = markups.map((m) => ({
        id: m.id,
        start_time: new Date(m.start).toISOString(),
        end_time: new Date(m.end).toISOString(),
      }));

      const res = await axios.post(
        `/api/folders/${selectedFolderId}/predict-ble/`,
        {
          segments: payload,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setMarkups((prev) => {
        const newMarkups = [...prev];
        res.data.segments.forEach((updatedSeg) => {
          const idx = newMarkups.findIndex((m) => m.id === updatedSeg.id);
          if (idx !== -1) {
            newMarkups[idx].ble_device_id = updatedSeg.ble_device_id || "";
          }
        });
        return newMarkups;
      });

      toast.success("BLE метки успешно предсказаны для текущих заездов!");
    } catch (err) {
      toast.error(
        "Ошибка предсказания BLE: " +
        (err.response?.data?.error || err.message),
      );
    } finally {
      setPredictingBle(false);
    }
  };

  const handleOmnicommSync = async () => {
    if (!selectedFolderId || markups.length === 0) return;

    if (!isSuperuser && omniCooldown > 0) {
      toast.warn(`Пожалуйста, подождите ${omniCooldown} сек перед повторным запросом.`);
      return;
    }

    setSyncingOmni(true);
    try {
      const token = localStorage.getItem("token");
      const payload = markups.map((m) => ({
        id: m.id,
        start_time: new Date(m.start).toISOString(),
        end_time: new Date(m.end).toISOString(),
      }));

      const res = await axios.post(
        `/api/folders/${selectedFolderId}/predict-omnicomm/`,
        { segments: payload },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setMarkups((prev) => {
        const newMarkups = [...prev];
        res.data.segments.forEach((updatedSeg) => {
          const idx = newMarkups.findIndex((m) => m.id === updatedSeg.id);
          if (idx !== -1) {
            newMarkups[idx].ble_device_id = updatedSeg.ble_device_id || "";
          }
        });
        return newMarkups;
      });

      toast.success("Связи Omnicomm успешно сопоставлены с вашими заездами!");
      if (!isSuperuser) setOmniCooldown(15);
    } catch (err) {
      toast.error(
        err.response?.data?.error || "Ошибка при сопоставлении с Omnicomm",
      );
    } finally {
      setSyncingOmni(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        e.target.tagName === "INPUT" ||
        e.target.tagName === "TEXTAREA" ||
        e.target.tagName === "SELECT"
      )
        return;

      if (e.key === "1") {
        e.preventDefault();
        if (!playerRef.current) return;
        const internalPlayer = playerRef.current.getInternalPlayer();
        if (!internalPlayer) return;

        const fallbackMs = folderStartTime
          ? folderStartTime.valueOf()
          : Date.now();
        const currentDbMs = videoSecToDbMs(
          internalPlayer.currentTime,
          framesMapping,
          fallbackMs,
        );

        setActiveStartMs((prevStart) => {
          if (prevStart === null) {
            toast.info("Начало заезда (Нажмите 1 для конца)");
            return currentDbMs;
          } else {
            if (currentDbMs > prevStart) {
              setMarkups((prev) => [
                ...prev,
                {
                  id: `m_${Date.now()}`,
                  start: prevStart,
                  end: currentDbMs,
                  ble_device_id: "",
                },
              ]);
              toast.success("Заезд добавлен!");
            } else {
              toast.warn("Конец заезда не может быть раньше начала");
            }
            return null;
          }
        });
      }
      if (e.key === "Escape" && activeStartMs !== null) {
        setActiveStartMs(null);
        toast.info("Отмена разметки");
      }
      if (e.code === "KeyS") {
        e.preventDefault();
        playerRef.current?.cycleSpeed?.();
        toast.info("Скорость изменена");
      }
      if (e.code === "Space") {
        e.preventDefault();
        playerRef.current?.togglePlay?.();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeStartMs, framesMapping, folderStartTime]);

  const handleProgress = (progressData) => {
    let sec = 0;
    if (typeof progressData === "number") sec = progressData;
    else if (progressData && typeof progressData.playedSeconds === "number")
      sec = progressData.playedSeconds;
    else return;

    if (framesMapping.length > 0) {
      const exactTimeMs = videoSecToDbMs(sec, framesMapping, 0);
      setCurrentVideoDbTimeMs(exactTimeMs);
    } else if (folderStartTime) {
      const fallbackMs = folderStartTime.valueOf();
      setCurrentVideoDbTimeMs(fallbackMs + Math.round(sec * 1000));
    }
  };

  const handleSeekDoubleClick = (exactTimeMs) => {
    if (!exactTimeMs) return;
    const fallbackMs = folderStartTime ? folderStartTime.valueOf() : Date.now();
    const videoSec = dbMsToVideoSec(exactTimeMs, framesMapping, fallbackMs);
    setSeekToTime(videoSec);
    setSeekTriggerId(Date.now());
    toast.info("Перемотка видео");
  };

  const deleteSegment = (idToRemove) =>
    setMarkups((prev) => prev.filter((m) => m.id !== idToRemove));

  return (
    <div className="bg-light min-vh-100 p-4">
      <CContainer fluid>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Студия разметки ИИ (Ground Truth)</h2>
          <CButton color="secondary" as={Link} to="/dashboard">
            <CIcon icon={cilArrowLeft} className="me-2" /> Назад
          </CButton>
        </div>

        <CRow>
          <CCol xl={3}>
            <CCard className="mb-4">
              <CCardHeader className="fw-bold">1. Выбор данных</CCardHeader>
              <CCardBody>
                {isSuperuser && (
                  <div className="mb-3">
                    <label className="form-label text-muted small">
                      Организация:
                    </label>
                    <CFormSelect
                      value={orgFilter}
                      onChange={(e) => {
                        setOrgFilter(e.target.value);
                        setSelectedDeviceId("");
                        setFolders([]);
                        setVideoUrl(null);
                      }}
                      size="sm"
                    >
                      <option value="">-- Все организации --</option>
                      {uniqueOrganizations.map((org) => (
                        <option key={org} value={org}>
                          {org}
                        </option>
                      ))}
                    </CFormSelect>
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label text-muted small">
                    Поиск экскаватора:
                  </label>
                  <CFormInput
                    type="text"
                    placeholder="Введите имя..."
                    value={deviceSearchQuery}
                    onChange={(e) => setDeviceSearchQuery(e.target.value)}
                    size="sm"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label text-muted small">
                    Экскаватор:
                  </label>
                  <CFormSelect
                    value={selectedDeviceId}
                    onChange={handleDeviceChange}
                  >
                    <option value="">-- Выберите --</option>
                    {filteredDevices.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name} {isSuperuser && d.organization_name ? ` (${d.organization_name})` : ""}
                      </option>
                    ))}
                  </CFormSelect>
                </div>

                <div className="mb-4">
                  <label className="form-label text-muted small">
                    Час видео:
                  </label>
                  {loading ? (
                    <div className="text-center">
                      <CSpinner size="sm" />
                    </div>
                  ) : (
                    <CFormSelect
                      value={selectedFolderId}
                      onChange={handleFolderChange}
                      disabled={!selectedDeviceId}
                      size="sm"
                      htmlSize={10}
                    >
                      {folders.map((f) => {
                        const tzOffset =
                          devices.find(
                            (d) => d.id === parseInt(selectedDeviceId),
                          )?.timezone_offset || 0;
                        const end = dayjs(f.process_after)
                          .utcOffset(getDeviceOffsetMin(tzOffset))
                          .startOf("hour");
                        const start = end.subtract(1, "hour");

                        let statusBadge = "❌ ";
                        if (f.is_labeled) {
                          statusBadge =
                            f.dataset_split === "valid"
                              ? "✅ [VAL]"
                              : "✅ [TRAIN]";
                        }

                        return (
                          <option key={f.id} value={f.id}>
                            {statusBadge} {start.format("DD.MM HH:00")} -{" "}
                            {end.format("HH:00")}
                          </option>
                        );
                      })}
                    </CFormSelect>
                  )}
                </div>

                <div className="p-3 bg-light border rounded">
                  <label className="form-label fw-bold mb-2">
                    Назначение выборки:
                  </label>
                  <div>
                    <CFormCheck
                      inline
                      type="radio"
                      name="split"
                      id="splitTrain"
                      label="Train (Обучение)"
                      checked={datasetSplit === "train"}
                      onChange={() => setDatasetSplit("train")}
                      disabled={!selectedFolderId}
                    />
                    <CFormCheck
                      inline
                      type="radio"
                      name="split"
                      id="splitValid"
                      label="Valid (Валидация)"
                      checked={datasetSplit === "valid"}
                      onChange={() => setDatasetSplit("valid")}
                      disabled={!selectedFolderId}
                    />
                  </div>
                </div>
              </CCardBody>
            </CCard>

            <CCard className="mb-4 shadow-sm">
              <CCardHeader className="fw-bold bg-white d-flex align-items-center">
                <CIcon icon={cilList} className="me-2 text-primary" />
                Размеченные данные
              </CCardHeader>
              <CCardBody className="p-0">
                {loadingSummary ? (
                  <div className="text-center p-4">
                    <CSpinner size="sm" color="primary" />
                  </div>
                ) : labeledSummary.length === 0 ? (
                  <div className="text-muted small p-4 text-center">
                    Нет размеченных папок
                  </div>
                ) : (
                  <div style={{ maxHeight: "450px", overflowY: "auto" }}>
                    {labeledSummary.map((group) => (
                      <div key={group.device.id} className="border-bottom">
                        <div
                          className="bg-light px-3 py-2 fw-bold text-dark border-bottom"
                          style={{ fontSize: "0.9rem" }}
                        >
                          {group.device.name}
                        </div>
                        <div className="list-group list-group-flush">
                          {group.folders.map((f) => {
                            const tzOffset = group.device.timezone_offset || 0;
                            const end = dayjs(f.process_after)
                              .utcOffset(getDeviceOffsetMin(tzOffset))
                              .startOf("hour");
                            const start = end.subtract(1, "hour");
                            const isVal = f.dataset_split === "valid";
                            const isSelected =
                              selectedFolderId === f.id.toString();

                            return (
                              <button
                                key={f.id}
                                type="button"
                                className={`list-group-item list-group-item-action px-3 py-2 d-flex justify-content-between align-items-center ${isSelected ? "active text-white" : ""}`}
                                onClick={() =>
                                  handleSelectFromSummary(group.device.id, f.id)
                                }
                                style={{
                                  cursor: "pointer",
                                  fontSize: "0.85rem",
                                }}
                              >
                                <span>
                                  {start.format("DD.MM HH:00")} -{" "}
                                  {end.format("HH:00")}
                                </span>
                                <CBadge
                                  color={isVal ? "warning" : "success"}
                                  shape="rounded-pill"
                                  className={
                                    isSelected ? "bg-white text-dark" : ""
                                  }
                                >
                                  {isVal ? "VAL" : "TRAIN"}
                                </CBadge>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CCardBody>
            </CCard>
          </CCol>

          <CCol xl={9}>
            <CCard>
              <CCardHeader className="d-flex justify-content-between align-items-center bg-white">
                <span className="fw-bold">
                  2. Синхронный редактор таймлайнов
                </span>
                <div className="d-flex align-items-center">
                  {selectedFolderId && videoUrl && (
                    <div className="me-4 d-flex align-items-center">
                      <span className="me-2 text-muted small">
                        Время в работе:
                      </span>
                      <CBadge
                        color={isUserActive ? "success" : "secondary"}
                        shape="rounded-pill"
                        style={{ width: "100px" }}
                      >
                        {isUserActive ? "⏱ " : "⏸ "}{" "}
                        {formatActiveTime(activeTimeSec)}
                      </CBadge>
                    </div>
                  )}

                  <CButton
                    color="info"
                    className="me-2 text-white"
                    onClick={handleAIPredict}
                    disabled={!selectedFolderId || predicting}
                  >
                    {predicting ? (
                      <CSpinner size="sm" />
                    ) : (
                      <>
                        <CIcon icon={cibLinuxFoundation} className="me-1" />{" "}
                        Предсказать ИИ
                      </>
                    )}
                  </CButton>

                  <CButton
                    color="warning"
                    className="me-2 text-white"
                    onClick={handleBLEPredict}
                    disabled={
                      !selectedFolderId || predictingBle || markups.length === 0
                    }
                  >
                    {predictingBle ? (
                      <CSpinner size="sm" />
                    ) : (
                      <>📡 Привязать BLE</>
                    )}
                  </CButton>

                  {currentDevice?.omnicomm_id && currentDevice?.has_omnicomm_config && (
                    <CButton
                      color="dark"
                      className="me-2 text-white"
                      onClick={handleOmnicommSync}
                      disabled={!selectedFolderId || syncingOmni || (!isSuperuser && omniCooldown > 0) || markups.length === 0}
                    >
                      {syncingOmni ? (
                        <CSpinner size="sm" className="me-1" />
                      ) : (
                        <span className="me-1">📡</span>
                      )}
                      {(!isSuperuser && omniCooldown > 0) ? `Ожидание (${omniCooldown}с)` : "Привязать Omnicomm"}
                    </CButton>
                  )}

                  <CButton
                    color="success"
                    onClick={saveMarkups}
                    disabled={!selectedFolderId || saving}
                  >
                    {saving ? (
                      <CSpinner size="sm" />
                    ) : (
                      <>
                        <CIcon icon={cilSave} className="me-1" /> Сохранить в БД
                      </>
                    )}
                  </CButton>
                </div>
              </CCardHeader>
              <CCardBody>
                <div className="alert alert-info py-2 small d-flex align-items-center">
                  <CIcon icon={cilInfo} className="me-2" size="2xl" />
                  <div>
                    <strong>Клавиша «1»</strong> — начало/конец заезда.{" "}
                    <strong>«Esc»</strong> — отменить.
                    <br />
                    <strong>Клавиша «S»</strong> — скорость.{" "}
                    <strong>«Пробел»</strong> — пауза.
                    <br />
                    <strong>ЛКМ</strong> — двигать/тянуть блоки.{" "}
                    <strong>ПКМ</strong> — удалить.{" "}
                    <strong>Двойной клик</strong> — перемотка видео.
                  </div>
                </div>

                {videoUrl ? (
                  <>
                    <div className="markup-video-wrapper mb-3">
                      <CustomVideoPlayer
                        ref={playerRef}
                        url={videoUrl}
                        initialSeekTime={seekToTime}
                        seekTriggerId={seekTriggerId}
                        onProgress={handleProgress}
                      />
                    </div>

                    <div className="position-relative">
                      {isTimelineLoading && (
                        <div
                          className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center"
                          style={{
                            zIndex: 50,
                            backgroundColor: "rgba(255, 255, 255, 0.9)",
                            backdropFilter: "blur(3px)",
                            borderRadius: "6px",
                          }}
                        >
                          <ThinkingOrb size="md" text="Нейросеть строит таймлайн..." />
                        </div>
                      )}
                      <SyncEditableTimeline
                        markups={markups}
                        setMarkups={setMarkups}
                        aiSessions={aiSessions}
                        blePoints={blePoints}
                        initialStart={visibleTimeStart}
                        initialEnd={visibleTimeEnd}
                        videoTimeMs={currentVideoDbTimeMs}
                        activeStartMs={activeStartMs}
                        targetHourStart={
                          folderStartTime ? folderStartTime.valueOf() : null
                        }
                        targetHourEnd={
                          folderStartTime
                            ? folderStartTime.valueOf() + 3600000
                            : null
                        }
                        timezoneOffset={currentDevice?.timezone_offset || 0}
                        onSeekDoubleClick={handleSeekDoubleClick}
                      />
                    </div>

                    <div className="mt-4">
                      <div className="d-flex justify-content-between align-items-end">
                        <h6 className="text-muted mb-0">
                          Таблица размеченных заездов: {markups.length} шт.
                        </h6>
                        <CBadge
                          color={
                            datasetSplit === "train" ? "primary" : "warning"
                          }
                          shape="rounded-pill"
                        >
                          Текущий набор: {datasetSplit.toUpperCase()}
                        </CBadge>
                      </div>

                      {markups.length > 0 ? (
                        <CTable
                          small
                          hover
                          responsive
                          bordered
                          className="mt-2 bg-white align-middle"
                        >
                          <CTableHead color="light">
                            <CTableRow>
                              <CTableHeaderCell
                                className="text-center"
                                style={{ width: "50px" }}
                              >
                                #
                              </CTableHeaderCell>
                              <CTableHeaderCell>Начало</CTableHeaderCell>
                              <CTableHeaderCell>Конец</CTableHeaderCell>
                              <CTableHeaderCell>Длительность</CTableHeaderCell>
                              <CTableHeaderCell>
                                Самосвал (BLE Метка)
                              </CTableHeaderCell>
                              <CTableHeaderCell
                                style={{ width: "80px" }}
                              ></CTableHeaderCell>
                            </CTableRow>
                          </CTableHead>
                          <CTableBody>
                            {markups
                              .sort((a, b) => a.start - b.start)
                              .map((seg, idx) => {
                                const selectedTag = availableTags.find(
                                  (t) =>
                                    t.id.toString() ===
                                    seg.ble_device_id?.toString(),
                                );
                                const selectBgColor = selectedTag
                                  ? getTagColor(selectedTag)
                                  : "#ffffff";
                                const selectTextColor = selectedTag
                                  ? "#ffffff"
                                  : "#000000";

                                const tzOffsetMin = getDeviceOffsetMin(currentDevice?.timezone_offset || 0);

                                return (
                                  <CTableRow key={seg.id}>
                                    <CTableDataCell className="text-center fw-bold text-muted">
                                      {idx + 1}
                                    </CTableDataCell>
                                    <CTableDataCell>
                                      {dayjs(seg.start).utcOffset(tzOffsetMin).format("HH:mm:ss")}
                                    </CTableDataCell>
                                    <CTableDataCell>
                                      {dayjs(seg.end).utcOffset(tzOffsetMin).format("HH:mm:ss")}
                                    </CTableDataCell>
                                    <CTableDataCell>
                                      {Math.round((seg.end - seg.start) / 1000)}{" "}
                                      сек
                                    </CTableDataCell>
                                    <CTableDataCell>
                                      <CFormSelect
                                        size="sm"
                                        value={seg.ble_device_id || ""}
                                        onChange={(e) => {
                                          const newVal = e.target.value;
                                          setMarkups((prev) =>
                                            prev.map((m) =>
                                              m.id === seg.id
                                                ? {
                                                  ...m,
                                                  ble_device_id: newVal,
                                                }
                                                : m,
                                            ),
                                          );
                                        }}
                                        style={{
                                          backgroundColor: selectBgColor,
                                          color: selectTextColor,
                                          fontWeight: "bold",
                                        }}
                                      >
                                        <option
                                          value=""
                                          style={{
                                            backgroundColor: "#fff",
                                            color: "#000",
                                          }}
                                        >
                                          -- Без привязки --
                                        </option>
                                        {availableTags.map((tag) => {
                                          const optColor = getTagColor(tag);
                                          return (
                                            <option
                                              key={tag.id}
                                              value={tag.id}
                                              style={{
                                                backgroundColor: optColor,
                                                color: "#fff",
                                              }}
                                            >
                                              {tag.truck} ({tag.mac})
                                            </option>
                                          );
                                        })}
                                      </CFormSelect>
                                    </CTableDataCell>
                                    <CTableDataCell className="text-center">
                                      <CButton
                                        size="sm"
                                        color="danger"
                                        variant="ghost"
                                        onClick={() => deleteSegment(seg.id)}
                                      >
                                        <CIcon icon={cilTrash} />
                                      </CButton>
                                    </CTableDataCell>
                                  </CTableRow>
                                );
                              })}
                          </CTableBody>
                        </CTable>
                      ) : (
                        <div className="text-muted small mt-2">
                          Пока нет размеченных сегментов.
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div
                    className="text-center text-muted p-5"
                    style={{ border: "2px dashed #ccc", borderRadius: "8px" }}
                  >
                    <h4>Выберите папку слева</h4>
                  </div>
                )}
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  );
};

export default MarkupPage;